import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { triggerHaptic } from '../utils/nativeBridge';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const ToastItem = ({ toast, onDismiss, getIcon }) => {
  const touchStartY = useRef(null);
  const [dragY, setDragY] = useState(0);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (touchStartY.current === null) return;
    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (deltaY < 0) {
      setDragY(deltaY);
    }
  };

  const handleTouchEnd = () => {
    if (dragY < -20) {
      triggerHaptic('light');
      onDismiss(toast.id);
    } else {
      setDragY(0);
    }
    touchStartY.current = null;
  };

  return (
    <div
      role="status"
      onClick={() => {
        triggerHaptic('light');
        onDismiss(toast.id);
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={dragY < 0 ? { transform: `translate3d(0, ${dragY}px, 0)` } : undefined}
      className={`pointer-events-auto flex items-center gap-2.5 px-4 py-2 sm:py-2.5 rounded-full bg-zinc-950/85 dark:bg-zinc-900/90 text-white shadow-[0_12px_36px_rgba(0,0,0,0.36),0_2px_8px_rgba(0,0,0,0.2)] backdrop-blur-2xl border border-white/15 ring-1 ring-black/20 select-none cursor-pointer active:scale-95 transition-transform duration-150 ${
        toast.isExiting ? 'native-toast-exit' : 'native-toast-enter'
      }`}
    >
      {getIcon(toast.type)}
      <span className="text-[13px] font-medium tracking-tight text-white/95 truncate max-w-[280px] sm:max-w-md">
        {toast.message}
      </span>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const exitingTimeoutsRef = useRef(new Map());

  const removeToast = useCallback((id) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, isExiting: true } : t))
    );
    const timer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      exitingTimeoutsRef.current.delete(id);
    }, 200);
    exitingTimeoutsRef.current.set(id, timer);
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 3500) => {
    triggerHaptic(type === 'error' ? 'error' : type === 'success' ? 'success' : 'light');
    const id = Date.now() + Math.random().toString(36).substring(2, 7);
    setToasts((prev) => [...prev, { id, message, type, isExiting: false }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, [removeToast]);

  const toast = {
    success: (msg, duration) => addToast(msg, 'success', duration),
    error: (msg, duration) => addToast(msg, 'error', duration),
    info: (msg, duration) => addToast(msg, 'info', duration),
  };

  const getToastIcon = (type) => {
    switch (type) {
      case 'success':
        return (
          <div className="w-5 h-5 rounded-full bg-emerald-500/25 text-emerald-400 ring-1 ring-emerald-400/30 flex items-center justify-center flex-shrink-0 text-[10px]">
            <i className="fas fa-check" />
          </div>
        );
      case 'error':
        return (
          <div className="w-5 h-5 rounded-full bg-rose-500/25 text-rose-400 ring-1 ring-rose-400/30 flex items-center justify-center flex-shrink-0 text-[10px]">
            <i className="fas fa-exclamation" />
          </div>
        );
      default:
        return (
          <div className="w-5 h-5 rounded-full bg-sky-500/25 text-sky-400 ring-1 ring-sky-400/30 flex items-center justify-center flex-shrink-0 text-[10px]">
            <i className="fas fa-info" />
          </div>
        );
    }
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Floating Native iOS HUD Toast Container */}
      <div
        className="fixed top-0 left-0 right-0 z-[10000] pointer-events-none flex flex-col items-center pt-[calc(env(safe-area-inset-top,0px)+0.65rem)] px-4 gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <ToastItem
            key={t.id}
            toast={t}
            onDismiss={removeToast}
            getIcon={getToastIcon}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
